import type { DocViewerKind } from '../../../lib/doc-viewer';

/** How the box around `DocViewerBody` should size itself for a kind.
 *
 *  - `fill`    — take the reading height. A picture, a video frame, a report or a table wants the room.
 *  - `framed`  — the content scrolls ITSELF (html, inside its sandboxed iframe), so the box must not add a
 *                second scrollbar.
 *  - `compact` — size to the content. An audio player is ~60px tall; letting it fill the reading height left
 *                it floating in a large white void.
 */
export type DocViewerBodyFit = 'fill' | 'framed' | 'compact';

/** The DECISION, shared by every host that renders `DocViewerBody`; the CLASS stays each host's own.
 *
 *  Two hosts render that body and their base boxes are genuinely different — `DocViewerModal`'s `.body` is the
 *  left column of a two-column layout that stretches to the card's capped reading height, `MobileFileDetailView`'s
 *  `.viewer` is a flexing column inside a scrolling phone page. So this cannot hand back a finished class name
 *  without one host inheriting the other's sizing, and it must not: they are different surfaces on purpose.
 *
 *  `compact` is honoured only by the phone page — the modal's two-column body already sizes the audio player
 *  beside its details column, so it maps `compact` to no modifier.
 *
 *  What it shares instead is the per-kind judgement, which is the part that was actually duplicated — both
 *  hosts carried their own inline `kind === 'html' ? bodyFramed : ''`, and adding a second condition would
 *  have meant a second thing to keep in step across two files.
 */
export function docViewerBodyFit(kind: DocViewerKind | null): DocViewerBodyFit {
  if (kind === 'html') return 'framed';
  if (kind === 'audio') return 'compact';
  return 'fill';
}
